import { Check, Trash2, X } from 'lucide-react';

import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui';


/**
 * App modal + confirm-dialog primitives (audit d31ac04b). Previously defined in
 * `pages/Settings/controls.jsx` yet imported by 16 files across nearly every
 * page — a placement inconsistency. They live here (their proper shared home)
 * built on the shared `Dialog` primitive; `pages/Settings/controls.jsx`
 * re-exports them so the many existing import sites are unchanged.
 *
 * NOTE: not re-exported from `components/ui/index.jsx` — that barrel already
 * exports a different `Modal` (the Radix `Dialog` alias in `Dialog.jsx`). Import
 * these two straight from `@src/components/ui/dialogs`.
 */


/**
 * Centered modal shell reused by the channel + location editors and the Security
 * transfer/delete modals. Built on the shared `Dialog` primitive (focus trap,
 * ESC, scroll-lock, `bg-scrim`). `footer` renders pinned at the bottom. The
 * desktop card / mobile full-screen split is preserved via `contentClassName`.
 */
function Modal({ title, subtitle, onClose, children, footer, width = 'lg:max-w-[480px]' }) {
  return (
    <Dialog open onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent
        variant="modal"
        className={cn(
          'max-lg:inset-0 max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-full max-lg:max-w-none',
          'max-lg:translate-x-0 max-lg:translate-y-0 max-lg:rounded-none max-lg:border-0',
          'gap-0 p-0',
          width,
        )}
      >
        <DialogHeader className="flex flex-row items-start justify-between gap-3 px-[22px] pb-3.5 pt-5">
          <span className="flex flex-col gap-1">
            <DialogTitle className="text-lg tracking-[-0.3px]">{title}</DialogTitle>

            {subtitle
              ? <DialogDescription className="text-table text-ink-muted">{subtitle}</DialogDescription>
              : null}
          </span>

          <Button
            size="icon"
            type="button"
            variant="ghost"
            aria-label="Close"
            className="size-[30px] shrink-0 rounded-md bg-surface-2"
            onClick={onClose}
          >
            <X className="size-4 text-ink-muted" />
          </Button>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[22px] pb-2.5">
          {children}
        </div>

        {footer
          ? (
            <DialogFooter className="border-t border-hairline px-[22px] py-3.5">
              {footer}
            </DialogFooter>
          )
          : null}
      </DialogContent>
    </Dialog>
  );
}

Modal.displayName = 'Modal';


/** Small confirm dialog — destructive (`danger`, default) or affirmative
 * (`primary`, e.g. plan activation). Built on the shared `Dialog`/`Button`
 * primitives. */
function ConfirmDialog(props) {
  const { title, body, confirmLabel = 'Delete', variant = 'danger', icon, busy, onCancel, onConfirm } = props;
  // Default icon adapts to intent: affirmative confirms (e.g. plan activation,
  // change-role) get a check, destructive ones a trash. Callers can override.
  const Icon = icon || (variant === 'primary' ? Check : Trash2);

  return (
    <Dialog open onOpenChange={(next) => (next ? null : onCancel())}>
      <DialogContent className="max-w-[420px] max-lg:rounded-xl">
        <DialogHeader className="max-lg:items-center max-lg:text-center">
          {/* Icon circle is a mobile-only affordance (.pen M Confirm Delete);
              desktop confirm dialogs stay icon-less + left-aligned (unchanged). */}
          <span
            className={cn(
              'grid size-[52px] place-items-center rounded-full lg:hidden',
              variant === 'primary' ? 'bg-primary-soft text-primary' : 'bg-danger-soft text-danger',
            )}
          >
            <Icon className="size-6" />
          </span>

          <DialogTitle>{title}</DialogTitle>

          {body
            ? <DialogDescription className="text-sm text-ink-muted">{body}</DialogDescription>
            : null}
        </DialogHeader>

        <DialogFooter className="max-lg:flex-col-reverse max-lg:gap-2.5">
          <Button type="button" variant="ghost" className="max-lg:w-full" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            type="button"
            className="max-lg:w-full"
            variant={variant === 'primary' ? 'primary' : 'danger'}
            loading={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ConfirmDialog.displayName = 'ConfirmDialog';


export { ConfirmDialog, Modal };
